import { Controller } from '@hotwired/stimulus';

// Drag-and-drop uploader for the self-service people import. Unlike the
// library's uploader this never uploads by XHR — a dropped or picked file is
// placed into the form's file input and the form submits to the preview page,
// so the server-side flow stays a plain form post.
export default class extends Controller {
  static targets = ['dropZone', 'fileInput', 'form', 'busyText', 'error'];

  connect() {
    // dragenter/dragleave also fire for child elements; track depth so the
    // highlight doesn't flicker while moving across the drop zone.
    this.dragDepth = 0;
    this.submitted = false;
  }

  browse(event) {
    // The "browse" label already opens the picker natively; don't open twice.
    if (event.target.closest('.import-upload-browse')) return;
    if (this.submitted) return;
    this.fileInputTarget.click();
  }

  dragEnter(event) {
    event.preventDefault();
    this.dragDepth++;
    this.dropZoneTarget.classList.add('drag-over');
  }

  dragOver(event) {
    event.preventDefault();
    this.dropZoneTarget.classList.add('drag-over');
  }

  dragLeave(event) {
    event.preventDefault();
    this.dragDepth = Math.max(0, this.dragDepth - 1);
    if (this.dragDepth === 0) {
      this.dropZoneTarget.classList.remove('drag-over');
    }
  }

  drop(event) {
    event.preventDefault();
    this.dragDepth = 0;
    this.dropZoneTarget.classList.remove('drag-over');

    const files = event.dataTransfer.files;
    if (files.length === 0) return;

    const transfer = new DataTransfer();
    transfer.items.add(files[0]);
    this.fileInputTarget.files = transfer.files;

    this.read(files[0]);
  }

  selectFile(event) {
    const file = event.target.files[0];
    if (file) this.read(file);
  }

  read(file) {
    if (this.submitted) return;

    const problem = this.problemWith(file);
    if (problem) {
      this.showError(problem);
      return;
    }

    this.submitted = true;
    this.hideError();
    this.busyTextTarget.textContent = `Reading ${file.name}...`;
    this.dropZoneTarget.classList.add('is-reading');
    this.formTarget.submit();
  }

  problemWith(file) {
    const extension = (file.name.split('.').pop() || '').toLowerCase();
    if (!['csv', 'txt', 'tsv'].includes(extension)) {
      return `${file.name} isn't a file we can read. Export your client list as a CSV and try again — in a spreadsheet, that's File → Save As → CSV.`;
    }

    const FIVE_MEGABYTES = 5 * 1024 * 1024;
    if (file.size > FIVE_MEGABYTES) {
      return `${file.name} is larger than the 5MB limit. Split it into smaller files and import them one at a time.`;
    }

    return null;
  }

  showError(message) {
    this.errorTarget.textContent = message;
    this.errorTarget.style.display = 'block';
  }

  hideError() {
    this.errorTarget.style.display = 'none';
  }
}
